import { ContainerTranslation } from "./container-translation";
import { Translatable } from "./translatable.interface";
import { Keyword } from "./keyword";

export class Container implements Translatable {
    id: number;
    columns = 1;
    keywords: Keyword[] = [];
    translations: ContainerTranslation[] = [];

    tempId: number = parseInt((Math.random() + 1).toString().replace('.', ''), 0);
    duplicatedKeyword = false;

    /**
     * Constructor
     *
     */
    constructor(init?: Container) {
        if (!!init) {
            Object.assign(this, init);
            this.translations = this.translations.map(t => new ContainerTranslation(t));
        }

        this.keywords = this.keywords.map(keyword => new Keyword(keyword));
    }

    /**
     * get translated attributes
     *
     * @param locale
     *
     * @return {ContainerTranslation}
     */
    public translateOrNew(locale: string) {
        let currentTranslation = this.translations.filter(translation => translation.locale === locale)[0];

        if (!currentTranslation) {
            currentTranslation = new ContainerTranslation;
            currentTranslation.locale = locale;

            this.translations.push(currentTranslation);
        }

        return currentTranslation;
    }

    /**
     * translate
     *
     * @param locale
     * @param defaultLocale
     * @return {ContainerTranslation}
     */
    public translate(locale: string, defaultLocale?: string) {
        return this.translations.filter(translation => { return translation.locale === locale; })[0] ||
            this.translations.filter(translation => { return translation.locale === defaultLocale; })[0] ||
            new ContainerTranslation();
    }

    /**
     * Get the id or a temporary id as fallback, if no id is present
     *
     * @param fallback
     */
    public getId(fallback: boolean = false) {
        return this.id || (fallback ? this.tempId : this.id);
    }

    /**
     * Add keyword (only if it doesn't exists yet)
     *
     * @param value
     * @param editLang
     */
    public addKeyword(value: string, editLang: string) {
        this.duplicatedKeyword = false;
        if (value.trim() !== '') {
            // check, if the keyword already exists
            for (let k = 0; k < this.keywords.length; k++) {
                if (this.keywords[k].translateOrNew(editLang).name === value.trim()) {
                    this.duplicatedKeyword = true;
                }
            }
            if (!this.duplicatedKeyword) {
                const keyword = new Keyword;
                keyword.translateOrNew(editLang).name = value.trim();
                this.keywords.push(keyword);
                this.orderKeywordsByName(editLang);
            }
        }
    }

    /**
     * Delete keyword
     *
     * @param id
     */
    public deleteKeyword(id: number) {
        for (let i = 0; i < this.keywords.length; i++) {
            if (this.keywords[i].getId(true) === id) {
                this.keywords.splice(i, 1);
            }
        }
    }

    /**
     * Order keywords by name
     *
     * @param lang
     * @param defaultLang
     */
    public orderKeywordsByName(lang, defaultLang?) {
        this.keywords = this.keywords.sort(function(a, b) {
            const nameA = (a.translate(lang, defaultLang).name || '').toUpperCase(); // ignore upper and lowercase
            const nameB = (b.translate(lang, defaultLang).name || '').toUpperCase(); // ignore upper and lowercase
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        });
    }

    /**
     * Removes a translation (to get not submitted if deactivated)
     *
     * @param locale
     */
    public removeTranslation(locale: string) {
        this.translations.forEach((translation, index) => {
            if (translation.locale === locale) {
                this.translations.splice(index, 1);
            }
        });

        this.keywords.forEach((keyword) => {
            keyword.removeTranslation(locale);
        });
    }

    /**
     * Check, if the container has at least one valid (in all available languages translated) keyword
     *
     * @param locales
     * @returns {boolean}
     */
    public hasTranslatedKeywordsInLocales(locales) {
        for (let i = 0; i < this.keywords.length; i++) {
            if (this.keywords[i].hasTranslationsInLocales(locales)) {
                return true;
            }
        }
        return false;
    }

    /**
     * check if this container has enabled keywords for user
     *
     * @param user
     * @return {boolean}
     */
    public hasEnabledKeywords(user) {
        for (let k = 0; k < user.selected_participant.keywords.length; k++) {
            const item = user.selected_participant.keywords[k];
            if (item.container_id === this.id) {
                return true;
            }
        }

        return false;
    }
}
